/**
 * usePlanFeatures — fetch + cache the caller org's plan feature bundle.
 *
 * Kept deliberately simple: a module-level cache + in-flight promise
 * so the hook can be used from multiple components (Sidebar, upgrade
 * prompts, page-level gates) without re-fetching on every mount. The
 * features change only on billing state transitions, so a TanStack-Query
 * or SWR setup would be overkill here.
 *
 * Consumers should treat `features === null` as "still loading" and render
 * a sensible default (e.g. hide Pro items until we know).
 */
import { useEffect, useState } from "react";
import { getPlanFeatures, type PlanFeatures } from "@/api/billing";
import { useAuth } from "./useAuth";

let _cache: PlanFeatures | null = null;
let _inflight: Promise<PlanFeatures> | null = null;
// Identity tag for the cached entry — see the hook body for why.
let _cacheFingerprint: string | null = null;

// Exposed so a successful checkout / portal return can force a refetch.
export function invalidatePlanFeatures(): void {
  _cache = null;
  _inflight = null;
}

async function loadPlanFeatures(): Promise<PlanFeatures> {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = getPlanFeatures()
    .then((f) => {
      _cache = f;
      return f;
    })
    .finally(() => {
      _inflight = null;
    });
  return _inflight;
}

interface UsePlanFeaturesResult {
  features: PlanFeatures | null;
  loading: boolean;
  error: string | null;
  /** Boolean helper that returns false while still loading. */
  has: (flag: keyof PlanFeatures) => boolean;
  /** Check whether a module key is visible under the current plan. */
  hasModule: (moduleKey: string) => boolean;
  refetch: () => Promise<void>;
}

export function usePlanFeatures(): UsePlanFeaturesResult {
  const { user } = useAuth();
  const [features, setFeatures] = useState<PlanFeatures | null>(_cache);
  const [loading, setLoading] = useState<boolean>(_cache === null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      // Unauthenticated — nothing to fetch. Drop any prior user's
      // cache so the next login doesn't see stale data.
      invalidatePlanFeatures();
      _cacheFingerprint = null;
      setFeatures(null);
      setLoading(false);
      return;
    }
    // If the user/org changed since the cache was populated, evict
    // it before the load. Common triggers: logout → login as a
    // different account in the same SPA session, post-checkout org
    // membership swap, or a fresh deploy that changed the trialing
    // tier shape (cache survives the deploy until the SPA reloads).
    const fp = user.id;
    if (_cacheFingerprint !== fp) {
      invalidatePlanFeatures();
      _cacheFingerprint = fp;
    }
    let cancelled = false;
    setLoading(_cache === null);
    loadPlanFeatures()
      .then((f) => {
        if (cancelled) return;
        setFeatures(f);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg =
          (err as { message?: string })?.message ||
          "Failed to load plan features.";
        setError(msg);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const has = (flag: keyof PlanFeatures): boolean => {
    if (!features) return false;
    const v = features[flag];
    return typeof v === "boolean" ? v : Boolean(v);
  };

  const hasModule = (moduleKey: string): boolean => {
    if (!features) return false;
    if (features.modules === "all") return true;
    return features.modules.includes(moduleKey);
  };

  const refetch = async (): Promise<void> => {
    invalidatePlanFeatures();
    setLoading(true);
    try {
      const f = await loadPlanFeatures();
      setFeatures(f);
      setError(null);
    } catch (err: unknown) {
      setError(
        (err as { message?: string })?.message ||
          "Failed to refresh plan features."
      );
    } finally {
      setLoading(false);
    }
  };

  return { features, loading, error, has, hasModule, refetch };
}
